/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { AlertCircle, Info, Loader2, ShieldCheck } from 'lucide-react';
import { APP_NAME } from '@/lib/branding';

/**
 * Known redirect error codes → friendly copy. A Map is used (not an object
 * literal) so an arbitrary `?error=` value cannot resolve to an inherited
 * Object.prototype member.
 */
const ERROR_MESSAGES = new Map<string, string>([
  ['access_denied', 'Your account does not currently have access. Contact your administrator.'],
  [
    'consent_required',
    'Additional Microsoft permissions are required. Contact your administrator.',
  ],
  ['login_failed', 'We could not sign you in. Please try again.'],
  ['session_expired', 'Your session expired. Sign in again to continue.'],
]);

const DEFAULT_ERROR = 'We could not sign you in. Please try again.';

interface SignInCardProps {
  /** Raw `error` query param, or null when there is none. */
  error: string | null;
  /** Starts the Microsoft SSO redirect. */
  onSignIn: () => void;
  /**
   * True from the click until the browser leaves for Microsoft. The MSAL
   * redirect is preceded by real network work, so without this the button is
   * visually inert for seconds and invites a second click.
   */
  pending?: boolean;
}

const SIGN_IN_LABEL = 'Continue with Microsoft work account';
const PENDING_LABEL = 'Redirecting to Microsoft…';

/** Inline Microsoft four-square logo (kept as raw SVG per brand guidance). */
function MicrosoftLogo() {
  return (
    <svg className="h-5 w-5 flex-none" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * The sign-in column: an audience chip, the page H1, an optional error banner,
 * the single Microsoft SSO button, a trust line, and a note saying what happens
 * after sign-in.
 *
 * Colours here are Amida's own (#175989 / #0F7A3D / #7E8C9C) rather than the
 * primary tokens, which still carry the previous product's blue. They are
 * page-local on purpose: swapping the global ramp reskins every screen at once
 * and that is a separate, wider change.
 *
 * Two resting-colour fixes ride along, both previously waived in
 * `e2e/public/sign-in.spec.ts`: the "Login here" divider (2.88:1) is gone, and
 * the error text moves to #B3261E, which is 5.72:1 on its own tint.
 */
export default function SignInCard({ error, onSignIn, pending = false }: SignInCardProps) {
  const errorMessage = error ? (ERROR_MESSAGES.get(error) ?? DEFAULT_ERROR) : null;

  // One centred group, legal line included, rather than a block anchored at the
  // top with a footer pinned to the bottom. That arrangement left a 334px hole
  // in the middle of the column, so the eye read two separate clumps instead of
  // one form — which is what made the column look crammed at the top and adrift
  // at the bottom at the same time. Spacing below is deliberately loose so the
  // group fills enough of the column for centring to look intentional.
  return (
    <div className="flex w-full max-w-[600px] flex-1 flex-col justify-center">
      <h1 className="text-[clamp(34px,3.4vw,46px)] font-extrabold leading-[1.04] tracking-[-0.042em] text-foreground">
        Sign in to {APP_NAME}
      </h1>

      {errorMessage && (
        <div
          role="alert"
          className="mt-6 flex items-start gap-2.5 rounded-lg border border-[#b3261e]/30 bg-[#fdecea] p-3 text-sm leading-snug text-[#b3261e]"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      <button
        type="button"
        onClick={onSignIn}
        disabled={pending}
        aria-busy={pending}
        aria-label={pending ? PENDING_LABEL : SIGN_IN_LABEL}
        className="mt-10 flex min-h-[56px] w-full items-center justify-center gap-3 rounded-[10px] border border-[#7e8c9c] bg-white px-4 text-[15px] font-semibold text-foreground transition hover:border-[#206b9f] hover:bg-[#f0f7fb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#206b9f] focus-visible:ring-offset-2 disabled:opacity-60"
      >
        {pending ? (
          <Loader2 className="h-5 w-5 flex-none animate-spin" aria-hidden="true" />
        ) : (
          <MicrosoftLogo />
        )}
        {pending ? PENDING_LABEL : 'Continue with Microsoft'}
      </button>

      {/*
        A live region OUTSIDE the button, because the button cannot announce its
        own pending state: setting `disabled` on the element the user just
        activated pulls it out of the focus order, focus falls to `body`, and
        neither the swapped `aria-label` nor the `aria-busy` transition is ever
        read out. Without this, the feedback this component adds is sighted-only
        — and an AT user who gets no feedback is exactly the person who clicks
        again, which is what manufactures a false `login_failed`.

        Always mounted with the text swapped, never conditionally rendered: a
        live region has to be in the DOM BEFORE its content changes or the
        change may not be announced. Same shape as HeaderSearch.tsx:303. A
        sibling of the button, not a child — a live region inside a disabled
        subtree is unreliable.
      */}
      <span role="status" aria-live="polite" className="sr-only">
        {pending ? PENDING_LABEL : ''}
      </span>

      <div className="mt-5 flex items-center justify-center gap-1.5 text-xs font-medium text-[#0f7a3d]">
        <ShieldCheck className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        Protected by Amida single sign-on
      </div>

      <div className="my-10 flex items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5b6b7a]">
        <span className="h-px flex-1 bg-border" />
        First time here?
        <span className="h-px flex-1 bg-border" />
      </div>

      <div className="flex gap-3 rounded-[11px] border border-border bg-[#f0f7fb] px-5 py-5">
        <Info className="mt-0.5 h-[17px] w-[17px] flex-none text-[#175989]" aria-hidden="true" />
        <p className="text-[13px] leading-relaxed text-[#4a5a68]">
          Courses are assigned and managed by your Amida admin, individually or by group. Sign in
          and whatever is waiting for you is already on your dashboard.
        </p>
      </div>

      <p className="mt-10 text-center text-xs leading-relaxed text-[#5b6b7a]">
        By signing in you agree to the{' '}
        <a
          href="https://www.amida.com/terms-and-conditions/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#175989] underline"
        >
          Terms and Conditions
        </a>{' '}
        and{' '}
        <a
          href="https://www.amida.com/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#175989] underline"
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}
